import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";

import { FaClipboardList, FaExclamationCircle, FaFileAlt, FaUserCheck, FaUserEdit, FaPlus } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import { useLocation } from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import { Backdrop } from "@material-ui/core";
import { SpeedDial, SpeedDialAction } from "@material-ui/lab";

import { dollarize } from "../../../../../../../utils/commonFunctions/dollar";
import { hideSSN } from "../../../../../../../utils/commonFunctions/SSN";
import { BrookshireBrothersCircleLogo, nexcaliberLogo } from "../../../../../../../assets";
import { api } from "../../../../../../../utils/api";
import { ClaimLines, EOBDetails } from "../../../../../../../@types/EOBDetails.types";
import { ThemeContext } from "../../../../../../../contexts";
import { Summary } from "../../../../../../../@types/components/EOB.types";
import { useQuery } from "../../../../../../../hooks";
import { Member } from "../../../../../../../@types/member.types";
import { dateTimeZoneConverter } from "../../../../../../../utils/commonFunctions/date";

import { HiBadgeCheck } from "react-icons/hi";
import AssessmentIcon from "@material-ui/icons/Assessment";

import "./explanationOfBenefitsV2.css";
import ExplanationOfBenefitsGrid from "./explanationOfBenefitsGrid/ExplanationOfBenefitsGrid";
import { NEXCALIBER_DETAILS } from "../../../../../../../constants/nexcaliberDetails";
import ExplanationOfBenefitFormPrint from "./explanationOfBenefitFormPrint/ExplanationOfBenefitFormPrint";

const ExplanationOfBenefitsV2: React.FC = (): JSX.Element => {
	const [openSpeedDial, setOpenSpeedDial] = useState<boolean>(false);
	const [EOBDetails, setEOBDetails] = useState<EOBDetails>();
	const [summary, setSummary] = useState<Summary>();
	const [member, setMember] = useState<Member>();

	const { state } = useLocation() as { state: { member: Member } };
	const { theme } = useContext(ThemeContext);
	const location = useLocation();
	const pathname = location.pathname;
	const query = useQuery();
	const componentRef = useRef(null);

	// const printableEligibleMembers: any[] = [
	// 	{
	// 		type_of_service: "dummy",
	// 		date_of_service: "05-24-2023",
	// 		total_charges: 54.25,
	// 		not_covered_or_penalty: "dummy",
	// 		discount: 10.12,
	// 		eligible_expense: "dummy",
	// 		remark_code: "DUMMY123",
	// 		co_play: "dummy",
	// 		deductable_amount: 15.26,
	// 		paid_at_percentage: 20.21,
	// 		benefit_paid: 23.25
	// 	},
	// 	{
	// 		type_of_service: "dummy",
	// 		date_of_service: "05-24-2023",
	// 		total_charges: 54.25,
	// 		not_covered_or_penalty: "dummy",
	// 		discount: 10.12,
	// 		eligible_expense: "dummy",
	// 		remark_code: "DUMMY123",
	// 		co_play: "dummy",
	// 		deductable_amount: 15.26,
	// 		paid_at_percentage: 20.21,
	// 		benefit_paid: 23.25
	// 	},
	// 	{
	// 		type_of_service: "dummy",
	// 		date_of_service: "05-24-2023",
	// 		total_charges: 54.25,
	// 		not_covered_or_penalty: "dummy",
	// 		discount: 10.12,
	// 		eligible_expense: "dummy",
	// 		remark_code: "DUMMY123",
	// 		co_play: "dummy",
	// 		deductable_amount: 15.26,
	// 		paid_at_percentage: 20.21,
	// 		benefit_paid: 23.25
	// 	}
	// ];
	const handlePrint = useReactToPrint({
		content: () => componentRef.current
	});

	const actions = [{ icon: <FaFileAlt />, name: "Download", handleAction: handlePrint }];

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);
	const handleCloseSpeedDial = () => setOpenSpeedDial(false);

	const getSummationByField = useCallback((claimLines: ClaimLines[], field: string) => {
		let summation = 0;
		claimLines.forEach((claimLine: any) => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			summation = summation + claimLine[field as keyof ClaimLines]!;
		});
		return summation;
	}, []);

	const calculateSummaryDetails = useCallback(
		(claimLines: ClaimLines[]) => {
			const totalAmountCovered = getSummationByField(claimLines, "ADJCVRD");
			const paidByOtherInsuranceCo = getSummationByField(claimLines, "COBAMT");
			const totalPaidByPlan = getSummationByField(claimLines, "PLNRESP");
			const employeeResponsibility = getSummationByField(claimLines, "EERESP");
			const surcharge = getSummationByField(claimLines, "EERESP");
			const withhold = getSummationByField(claimLines, "EERESP");
			const copay = getSummationByField(claimLines, "EERESP");
			setSummary(
				Object.assign(
					{},
					{
						totalAmountCovered,
						paidByOtherInsuranceCo,
						totalPaidByPlan,
						employeeResponsibility,
						surcharge,
						withhold,
						copay
					}
				)
			);
		},
		[getSummationByField]
	);

	const getEOBDetails = useCallback(
		async (params: {
			claim_pre: number;
			claim_number: number;
			claim_suffix: number;
			type: string;
		}): Promise<void> => {
			const token = localStorage.getItem("@jwt");
			if (token === null || member === undefined || member._id === undefined) {
				return;
			}
			// const redirectionResult = await trackPromise(api.redirection.redirectUser(member._id, token));
			const filter = {
				claim_number: params.claim_number,
				claim_prefix: params.claim_pre,
				claim_suffix: params.claim_suffix
			};
			const _EOBDetails = await trackPromise(api.claim.getEOBDetailsV2(filter));
			setEOBDetails(Object.assign({}, _EOBDetails));
			// calculateSummaryDetails(_EOBDetails.claimLines);
		},
		[member]
	);

	useEffect(() => {
		if (!query || !member || !location) return;

		let dependentNumber: number | null = null;

		if (pathname === "/dependent/claim/EOB") {
			const dependentNumberQuery = query.get("dn");
			if (dependentNumberQuery !== null && typeof dependentNumberQuery === "string") {
				dependentNumber = Number(atob(dependentNumberQuery));
			}
		} else {
			dependentNumber = member.dependent_number;
		}

		if (
			query.get("number") &&
			query.get("pre") &&
			query.get("suffix") &&
			query.get("type") &&
			dependentNumber !== null &&
			dependentNumber !== undefined
		) {
			const params = {
				claim_number: Number(query.get("number")),
				claim_pre: Number(query.get("pre")),
				claim_suffix: Number(query.get("suffix")),
				type: query.get("type") === "0" ? "CURRENT" : "HISTORY",
				dependent_number: dependentNumber
			};
			getEOBDetails(params);
		}
	}, [getEOBDetails, query, member, location, pathname]);

	useEffect(() => {
		// console.log("state", state);
		if (state.member) {
			setMember(Object.assign({}, state.member));
		}
	}, [state]);

	if (!EOBDetails || !summary || !member) return <></>;

	return (
		<div className="EOB-main" id="EOB-main">
			<Backdrop open={openSpeedDial} style={{ zIndex: 1009 }} />
			<SpeedDial
				ariaLabel="SpeedDial basic example"
				style={{
					position: "fixed",
					bottom: 16,
					right: 16
				}}
				icon={<FaPlus />}
				onOpen={handleOpenSpeedDial}
				onClose={handleCloseSpeedDial}
				open={openSpeedDial}
				FabProps={{
					style: {
						backgroundColor: theme?.primary_color
					}
				}}
			>
				{actions.map((action: any) => (
					<SpeedDialAction
						key={action.name}
						icon={action.icon}
						tooltipTitle={action.name}
						tooltipOpen
						onClick={action.handleAction}
					/>
				))}
			</SpeedDial>

			<div className="EOB-paper-container">
				{/* ============================= HEADER CONTAINER ================================= */}
				<div className="header-container">
					<div className="group-logo-container">
						<img src={BrookshireBrothersCircleLogo} className="group-logo" alt="" />
					</div>
					<div className="nexcaliber-logo-container">
						<img src={nexcaliberLogo} className="nexcaliber-logo" alt="" />
						<div className="nexcaliber-location-details">
							<span>Nexcaliber</span>
							<span>PO BOX 802422, Dalas Texas 75380</span>
							<span>{NEXCALIBER_DETAILS.NEXCALIBER_CONTACT}</span>
						</div>
					</div>
				</div>
				<div className="subheading-main" style={{ color: theme.primary_color }}>
					<HiBadgeCheck />
					Explanation of Benefits
				</div>
				{/* ============================= BODY CONTAINER ====================================*/}
				<div className="eob-grid-container">
					<div className="eob-grid-item">
						<div className="category-heading" style={{ color: theme.primary_color }}>
							<FaUserCheck />
							<span>Subscriber</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">First Name</span>
							<span className="field-value">
								{EOBDetails.claimant.patient_name.substring(
									0,
									EOBDetails.claimant.patient_name.indexOf(" ")
								)}
							</span>
						</div>
						<span className="grid-item-fields">
							<div className="grid-item-fields">
								<span className="field-title">Last Name</span>
								<span className="field-value">{EOBDetails.claimant.patient_name.split(" ")[1]}</span>
							</div>
						</span>
						<span className="grid-item-fields">
							<div className="grid-item-fields">
								<span className="field-title">Address</span>
								<span className="field-value">{member.address_line_1}</span>
								<span className="field-value">
									{member.city + ", " + member.state + " " + member.ZIP}
								</span>
							</div>
						</span>
					</div>
					<div className="eob-grid-item">
						<div className="category-heading" style={{ color: theme.primary_color }}>
							<FaUserEdit />
							<span>Claimant</span>
						</div>
						<div className="claimant-container">
							<div className="left-section">
								<div className="grid-item-fields">
									<span className="field-title">SSN</span>
									<span className="field-value">{hideSSN(EOBDetails.claimant.SSN)}</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">Claim For</span>
									<span className="field-value">{EOBDetails.claimant.patient_name}</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">Patient Number :</span>
									<span className="field-value">{EOBDetails.claimant.patient_account}</span>
								</div>

								<div className="grid-item-fields">
									<span className="field-title">Claim Number :</span>
									<span className="field-value">{EOBDetails.claimant.full_claim_number}</span>
								</div>
							</div>
							<div className="right-section">
								<div className="grid-item-fields">
									<span className="field-title">Paid Date :</span>
									<span className="field-value">
										{String(dateTimeZoneConverter(EOBDetails.paid_date))}
									</span>
								</div>
							</div>
						</div>
					</div>
					<div className="eob-grid-item">
						<div className="category-heading" style={{ color: theme.primary_color }}>
							<FaClipboardList />
							<span>Provider Details</span>
						</div>
						<span>{EOBDetails.provider.full_provider_number}</span>
						<span>{EOBDetails.provider.name}</span>
						<span>{EOBDetails.provider.address_line_1}</span>
						<span>
							{EOBDetails.provider.city}, {EOBDetails.provider.state}, {EOBDetails.provider.zipcode}
						</span>
						<span>{EOBDetails.provider.PPO}</span>
					</div>
					<div className="eob-grid-item">
						<div className="category-heading" style={{ color: theme.primary_color }}>
							<FaExclamationCircle />
							<span>Claim Summary</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Total Amount Covered:</span>
							<span className="field-value">{dollarize(summary.totalAmountCovered)}</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Paid by Insurrance company:</span>
							<span className="field-value">{dollarize(summary.paidByOtherInsuranceCo)}</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Total Paid By plans:</span>
							<span className="field-value">{dollarize(summary.totalPaidByPlan)}</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Employee Responsibility:</span>
							<span className="field-value">{dollarize(summary.copay)}</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Deductible Remaining:</span>
							<span className="field-value">N/A</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">In Network:</span>
							<span className="field-value">N/A</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Out OF Pocket Remaining Plan:</span>
							<span className="field-value">N/A</span>
						</div>
						<div className="grid-item-fields">
							<span className="field-title">Annual Accumulation:</span>
							<span className="field-value">N/A</span>
						</div>
					</div>
				</div>
				{/* ============================= CLAIM DETAILS ====================================*/}
				<div className="subheading-main" style={{ color: theme.primary_color }}>
					<AssessmentIcon />
					Claim Details
				</div>

				<ExplanationOfBenefitsGrid claimLines={EOBDetails.claimLines} />
			</div>
			{EOBDetails && summary ? (
				<div style={{ display: "none" }}>
					<Suspense fallback={<div />}>
						<ExplanationOfBenefitFormPrint
							EOBDetails={EOBDetails}
							member={member}
							summary={summary}
							ref={componentRef}
						/>
					</Suspense>
				</div>
			) : null}
		</div>
	);
};

export default ExplanationOfBenefitsV2;
